import shortId from 'shortid';

import {
  DATALINE_DEFINITION_MAX_LENGTH,
  DATALINE_DEFINITION_MAX_NUMBER,
  DATALINE_ERRORS,
  DatalineType,
  LogicalOperator,
  SHAPING_NAME_MAX_LENGTH,
} from 'src/constants/dataline';
import { DRAG_ITEM_TYPES } from 'src/constants/dragItem';
import { NO_REPORT_CONFIGURATION_VALUE } from 'src/constants/report';
import type {
  BackendDataline,
  ComparisonFrontend,
  DatalinePosition,
  DefinitionLineFrontend,
  OriginalDataline,
  RawOriginalDataline,
  ReportDatalineResponse,
  ReportDatalineWrapper,
} from 'src/types/dataline';
import type {
  ReportConfigAllocationCombination,
  ReportConfigCombinations,
} from 'src/types/reportConfig';
import type {
  DragItem,
  DragItemOrgUnit,
  DragItemVariable,
  ReportConfiguration,
} from 'src/types/workspace';

export const getPossibleOptions = (
  reportType: string,
  fileType: string,
  reportConfigCombinations?: ReportConfigCombinations,
) => {
  if (!reportConfigCombinations || !reportType || !fileType) {
    return {
      reportTypes: {},
      fileTypes: {},
      languages: {},
      comment: '',
    };
  }

  const structure = reportConfigCombinations?.structure || {};
  const content = reportConfigCombinations?.content || {};

  const fileTypes = Object.keys(structure?.[reportType] || {}) ?? [];
  const languages = structure?.[reportType]?.[fileType] ?? [];
  const comment =
    content.descriptions?.[reportType]?.[fileType]?.['description'] ?? '';

  return {
    reportTypes: Object.fromEntries(
      Object.entries(content?.reportTypes || {}).map(
        ([key, namePerFileType]) => [
          key,
          // display name corresponding to report type and file type
          fileType && namePerFileType?.[fileType]?.name
            ? namePerFileType[fileType].name
            : Object.values(namePerFileType)[0]?.name ?? '',
        ],
      ),
    ),
    fileTypes: Object.fromEntries(
      Object.entries(content?.fileTypes || {})
        .filter(([key]) => fileTypes.includes(key))
        .map(([key, value]) => [key, value.abbreviation]),
    ),
    languages: Object.fromEntries(
      Object.entries(content?.languages || {})
        .filter(([key]) => languages.includes(key))
        .map(([key, value]) => [key, value.name]),
    ),
    comment: comment,
  };
};

export const getReportConfiguration = (
  currentReport: ReportConfiguration,
  reportConfigData?: ReportConfigAllocationCombination,
) => {
  const structure = reportConfigData?.reportConfigCombinations?.structure ?? {};
  if (!Object.keys(structure).length) {
    return;
  }

  // use the first report type - file type - language combination available
  const reportType = Number(
    Object.keys(structure)?.[0] ?? NO_REPORT_CONFIGURATION_VALUE,
  );
  const fileType = Number(
    Object.keys(structure?.[reportType] || {})?.[0] ??
      NO_REPORT_CONFIGURATION_VALUE,
  );
  const language = Number(
    structure?.[reportType]?.[fileType]?.[0] ?? NO_REPORT_CONFIGURATION_VALUE,
  );
  if (
    [reportType, fileType, language].includes(NO_REPORT_CONFIGURATION_VALUE) ||
    [reportType, fileType, language].some(Number.isNaN)
  ) {
    return;
  }

  return {
    ...currentReport,
    fileType: fileType,
    language: language,
    reportType: reportType,
  };
};

export function findDataPosition(
  datalines: OriginalDataline[],
  findId: string,
) {
  const positions: DatalinePosition = {
    comparisonIndex: null,
    definitionlineIndex: null,
    datalineIndex: null,
  };

  positions.datalineIndex = datalines.findIndex((datalineObj) => {
    // find position of a dataline object in the dataline Array.
    // Used for: deleting/moving Dataline.
    // Returns an array like: [i]
    if (datalineObj.renderId === findId) return true;

    const { definitionlines } = datalineObj;
    if (!definitionlines?.length) return false;

    const definitionlinesIndex = definitionlines.findIndex((comparisonsObj) => {
      // find position of a comparison that is connected with a logical OR
      // Used for: Changing Or to AND
      // Returns an array like: [i,j]
      if (comparisonsObj.renderId === findId) return true;

      const comparisonsIndex = comparisonsObj.comparisons.findIndex(
        (comparison) =>
          // find position of a comparison that is connected with a logical AND
          // Used for: Changing And to Or and deleting shaping
          // Returns array like: [i,j,k]
          comparison.renderId === findId,
      );

      if (comparisonsIndex !== -1) {
        positions.comparisonIndex = comparisonsIndex;
        return true;
      }
      return false;
    });

    if (definitionlinesIndex !== -1) {
      positions.definitionlineIndex = definitionlinesIndex;
      return true;
    }
    return false;
  });

  return positions;
}

export function addObjectAfterPosition(
  datalinesObj: OriginalDataline[],
  atRenderId: string,
  addObject: ComparisonFrontend,
) {
  const datalines: OriginalDataline[] = [...datalinesObj]; // we copy the dataline to avoid pointer problems
  const positions = findDataPosition(datalines, atRenderId);
  const { datalineIndex, definitionlineIndex, comparisonIndex } = positions;

  if (datalineIndex === null || !datalines[datalineIndex]) {
    return datalines;
  }

  if (definitionlineIndex !== null && comparisonIndex === null) {
    datalines[datalineIndex].definitionlines[
      definitionlineIndex
    ].comparisons.push(addObject);
  }

  if (definitionlineIndex !== null && comparisonIndex !== null) {
    datalines[datalineIndex].definitionlines[
      definitionlineIndex
    ].comparisons.splice(comparisonIndex + 1, 0, addObject);
  }

  return datalines;
}

export function deleteObjectAtPosition(
  datalines: OriginalDataline[],
  atRenderId: string,
) {
  const positions = findDataPosition(datalines, atRenderId);
  const { datalineIndex, definitionlineIndex, comparisonIndex } = positions;

  if (datalineIndex === null || !datalines[datalineIndex]) {
    return datalines;
  }

  const adjustedDatalines = [...datalines];
  if (definitionlineIndex === null && comparisonIndex === null) {
    adjustedDatalines.splice(datalineIndex, 1);
  }

  if (definitionlineIndex !== null && comparisonIndex === null) {
    adjustedDatalines[datalineIndex].definitionlines.splice(
      definitionlineIndex,
      1,
    );
  }

  if (definitionlineIndex !== null && comparisonIndex !== null) {
    // Remove the current definitionline if its last comparison was deleted. But only if there is still
    // another definitionline left, so the user can still drop items into the current dataline.
    if (
      adjustedDatalines[datalineIndex].definitionlines.length > 1 &&
      adjustedDatalines[datalineIndex].definitionlines[definitionlineIndex]
        .comparisons.length === 1
    ) {
      adjustedDatalines[datalineIndex].definitionlines.splice(
        definitionlineIndex,
        1,
      );
    } else {
      adjustedDatalines[datalineIndex].definitionlines[
        definitionlineIndex
      ].comparisons.splice(comparisonIndex, 1);
      // The first comparison of a definitionline should always be positive
      const firstComparisons =
        adjustedDatalines[datalineIndex].definitionlines[definitionlineIndex]
          .comparisons[0];
      if (firstComparisons) {
        firstComparisons.op = LogicalOperator.AND;
      }
    }
  }

  return adjustedDatalines;
}

export function trimShapingName(shapingName: string) {
  return shapingName.length > SHAPING_NAME_MAX_LENGTH
    ? `${shapingName.substring(0, SHAPING_NAME_MAX_LENGTH)}... `
    : shapingName;
}

export function generateDatalineDefinition(originalDataline: OriginalDataline) {
  let newDatalineName = '';
  const dataline: OriginalDataline = JSON.parse(
    JSON.stringify(originalDataline),
  );

  dataline.definitionlines?.forEach((definitionline, definitionlineIndex) => {
    if (definitionlineIndex !== 0)
      newDatalineName = newDatalineName.concat(' OR ');

    definitionline.comparisons.forEach((comparison, comparisonIndex) => {
      const isOrgUnit = comparison.variable === DRAG_ITEM_TYPES.ORG_UNIT;

      const trimmedShapingNames = isOrgUnit
        ? comparison.valueNames.map(
            (valueName, index) =>
              `${trimShapingName(valueName)} ${comparison.values[index]}`,
          )
        : comparison.valueNames.map((valueName) => trimShapingName(valueName));

      if (comparisonIndex !== 0) {
        newDatalineName = newDatalineName.concat(
          `${comparison.op === LogicalOperator.AND ? ' AND ' : ' EXCLUDE '}`,
        );
      }

      if (isOrgUnit) {
        newDatalineName = newDatalineName.concat(
          trimmedShapingNames.join(', '),
        );
      } else {
        newDatalineName = newDatalineName.concat(
          `${comparison.variableName}: ${trimmedShapingNames.join(', ')}`,
        );
      }
    });
  });

  return { ...dataline, name: newDatalineName, has_custom_name: false };
}

export const mapDragItemToComparisonProperties = (
  dragItem: DragItem,
  renderId: string,
) => {
  let comparisonProps: ComparisonFrontend = {
    valueNames: [],
    values: [],
    variable: '',
    variableName: '',
    op: undefined,
    renderId: renderId,
  };

  switch (dragItem.dragSourceType) {
    case DRAG_ITEM_TYPES.DATALINE:
      break;

    case DRAG_ITEM_TYPES.ORG_UNIT: {
      const dragItemOrgUnit = dragItem as DragItemOrgUnit;
      comparisonProps = {
        valueNames: [
          dragItemOrgUnit.title
            ? dragItemOrgUnit.title
            : dragItemOrgUnit.orgUnitId,
        ],
        values: [dragItemOrgUnit.orgUnitId],
        variable: dragItem.dragSourceType,
        variableName: 'OU',
        renderId: renderId,
      };
      break;
    }

    case DRAG_ITEM_TYPES.SHAPING: {
      const dragItemVariable = dragItem as DragItemVariable;
      comparisonProps = {
        valueNames: [dragItemVariable.title],
        values: [dragItemVariable.shapingId],
        variable: dragItemVariable.variableId,
        variableName: dragItemVariable.variableShortName
          ? dragItemVariable.variableShortName
          : dragItemVariable.variableName,
        renderId: renderId,
      };
      break;
    }

    default:
      break;
  }

  return comparisonProps;
};

export function convertFrontendToBackendDatalines(
  datalines: OriginalDataline[],
) {
  const resDataline: ReportDatalineResponse[] = [];

  datalines.forEach((datalineObj, index) => {
    const resDefinitionline: BackendDataline[][] = [];

    datalineObj.definitionlines.forEach((definitionlineObj) => {
      const resComparison: BackendDataline[] = [];

      definitionlineObj.comparisons.forEach((comparisonObj) => {
        resComparison.push({
          variable: comparisonObj.variable,
          values: comparisonObj.values.map(String),
          op: comparisonObj.op || LogicalOperator.AND,
        });
      });

      resDefinitionline.push(resComparison);
    });

    resDataline.push({
      dataline: { data: resDefinitionline },
      position: index, // Only update the position when preparing for the endpoint as it is irrelevant for the frontend.
      name: datalineObj.name,
      has_custom_name: datalineObj.has_custom_name,
      driver_analysis: datalineObj.driverAnalysis,
    });
  });

  return resDataline;
}

export function convertBackendToFrontendDatalines(
  datalines: ReportDatalineWrapper[],
) {
  return datalines.map((datalineObject) => {
    const {
      // Use "parsed_data" instead of "data" as this contains extra fields for the frontend.
      dataline: {
        parsed_data: definitionlines,
        dataline_type: datalineType,
        ...datalineRest
      },
      is_global_template: isGlobalTemplate,
      locked: isLocked,
      driver_analysis,
      ...datalineObjectRest
    } = datalineObject;

    const isExternal = datalineType === DatalineType.EXTERNAL;
    const isReadOnly = isGlobalTemplate || isLocked || isExternal;
    // Rename "dataline.data" to "definitionlines" - lift and merge the "dataline" object - add render id.
    const processedDatalineObject: RawOriginalDataline = {
      definitionlines: definitionlines.map((definitionlineComparisons) => {
        const comparisons = definitionlineComparisons.map((comparison) => ({
          ...comparison,
          renderId: shortId.generate(),
          variableName: comparison.variable_short_name
            ? comparison.variable_short_name
            : comparison.variable,
          valueNames: comparison.value_names.length
            ? comparison.value_names
            : comparison.values,
        }));

        return {
          comparisons,
          renderId: shortId.generate(),
        };
      }),

      ...datalineRest,
      ...datalineObjectRest,
      isExternal,
      invalidities: false,
      isSaved: false,
      isReadOnly,
      driver_analysis: driver_analysis,
      driverAnalysis: driver_analysis,
      renderId: shortId.generate(),
    };

    return processedDatalineObject;
  });
}

export function joinDefinitionlines(
  operator: LogicalOperator,
  firstDefinitionline: DefinitionLineFrontend,
  secondDefinitionline: DefinitionLineFrontend,
) {
  const comparisonsToAdd = secondDefinitionline.comparisons;
  comparisonsToAdd[0].op = operator;

  return {
    renderId: shortId.generate(),
    comparisons: firstDefinitionline.comparisons.concat(comparisonsToAdd),
  };
}

export function splitDefinitionline(
  dataline: OriginalDataline,
  positions: DatalinePosition,
) {
  if (!dataline.definitionlines?.length) return null;

  const { definitionlineIndex, comparisonIndex } = positions;
  if (definitionlineIndex === null || comparisonIndex === null) return dataline;

  const currentDefinitionline = dataline.definitionlines[definitionlineIndex];
  const { comparisons } = currentDefinitionline;
  const preSplitComparisons = comparisons.slice(0, comparisonIndex);
  const postSplitComparisons = comparisons.slice(
    comparisonIndex,
    currentDefinitionline.comparisons.length,
  );
  postSplitComparisons[0].op = LogicalOperator.AND;

  dataline.definitionlines.splice(definitionlineIndex, 1, {
    renderId: shortId.generate(),
    comparisons: preSplitComparisons,
  });

  dataline.definitionlines.splice(definitionlineIndex + 1, 0, {
    renderId: shortId.generate(),
    comparisons: postSplitComparisons,
  });

  return dataline;
}

export function getDatalineErrors(dataline: OriginalDataline) {
  const { definitionlines, name } = dataline;
  const errors = [];

  // external datalines should not be validated, since they can't be altered by the frontend user
  if (dataline.isExternal) return [];

  if (name === undefined || name === null || name === '') {
    errors.push(DATALINE_ERRORS.UNNAMED);
  }

  if (name && name.length > DATALINE_DEFINITION_MAX_LENGTH) {
    errors.push(DATALINE_ERRORS.NAME_TOO_LONG);
  }

  definitionlines.forEach((definitionline) => {
    const { comparisons } = definitionline;
    if (comparisons.length === 0) {
      errors.push(DATALINE_ERRORS.EMPTY_DEFINITIONLINE);
    } else {
      const orgUnitIsSet = comparisons.find(
        (comparison) =>
          comparison.variable === DRAG_ITEM_TYPES.ORG_UNIT &&
          comparison.op === LogicalOperator.AND,
      );

      if (!orgUnitIsSet) {
        errors.push(DATALINE_ERRORS.MISSING_ORG_UNIT);
      }
    }
  });

  if (definitionlines.length > DATALINE_DEFINITION_MAX_NUMBER) {
    errors.push(DATALINE_ERRORS.TOO_MANY_DEFINITIONLINES);
  }

  return [...new Set(errors)];
}

export const validateDataline = (dataline: OriginalDataline) => {
  const errors = getDatalineErrors(dataline);
  return errors.length !== 0;
};
